import React from "react";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import { PieChart } from "@mui/x-charts/PieChart";
import { AnalyticsFilters, AnalyticsFiltersState } from "../components/AnalyticsFilters";
import {
    AnalyticsCard,
    BlockError,
    BlockLoading,
    EmptyState,
    MetricHint,
    WatchSignals,
} from "../components/AnalyticsShared";
import { AnalyticsSeriesChart } from "../components/AnalyticsSeriesChart";
import { useAsyncBlock } from "../hooks";
import { bidsFunnelApi } from "../../bidsApi";
import { BidFunnelReport } from "../../../../models/bidAnalytics";
import { formatInt } from "../format";
import { rollupSeries } from "../rollup";
import { Game } from "../../../../models/games";

type Props = {
    isActive: boolean;
    filters: AnalyticsFiltersState;
    onFiltersChange: (next: AnalyticsFiltersState) => void;
    games: Game[];
    gamesLoading: boolean;
};

export function FunnelTab(props: Props) {
    const { isActive, filters, onFiltersChange, games, gamesLoading } = props;

    const [state, refresh] = useAsyncBlock<BidFunnelReport>(
        () =>
            bidsFunnelApi({
                from: filters.from,
                to: filters.to,
                gameId: filters.gameId,
            }),
        isActive,
        [filters.from, filters.to, filters.gameId]
    );

    const totals = state.data?.totals;
    const chartData = React.useMemo(
        () =>
            rollupSeries(state.data?.series ?? [], filters.granularity, {
                sumKeys: [
                    "totalCreated",
                    "activeCount",
                    "completedCount",
                    "reservedQty",
                    "unfilledClosedQty",
                ],
            }),
        [state.data?.series, filters.granularity]
    );

    const pieData = totals
        ? [
              { id: 0, value: totals.activeCount, label: "Остаток", color: "#1976d2" },
              { id: 1, value: totals.completedCount, label: "Куплено", color: "#2e7d32" },
              { id: 2, value: totals.reservedQty, label: "В резерве", color: "#f9a825" },
              { id: 3, value: totals.unfilledClosedQty, label: "Закрыто без покупки", color: "#c62828" },
          ].filter((x) => x.value > 0)
        : [];

    const isEmpty = totals != null && totals.totalCreated === 0;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <AnalyticsFilters
                value={filters}
                onChange={onFiltersChange}
                onRefresh={refresh}
                loading={state.loading}
                games={games}
                gamesLoading={gamesLoading}
                showPeriod
                showGranularity
            />

            <AnalyticsCard
                title="Что происходит с позициями заявок"
                subtitle="Считаем штуки, а не заявки целиком. Купленное включает недоисполненные заявки; остаток — и офлайн, и выключенные."
                snapshotLabel="period"
                howToRead={
                    <List size="sm" marker="disc">
                        <ListItem>
                            <Typography level="body-sm">
                                <strong>Создано</strong> — сколько штук запросили в заявках периода
                            </Typography>
                        </ListItem>
                        <ListItem>
                            <Typography level="body-sm">
                                <strong>Остаток шт.</strong> — свободные позиции открытых заявок (Count − Bought −
                                Reserved), в том числе если юзер офлайн или заявки выключены
                            </Typography>
                        </ListItem>
                        <ListItem>
                            <Typography level="body-sm">
                                <strong>Куплено шт.</strong> — сумма купленных позиций по всем заявкам, даже ещё не
                                закрытым
                            </Typography>
                        </ListItem>
                        <ListItem>
                            <Typography level="body-sm">
                                <strong>В резерве</strong> — позиции в процессе покупки
                            </Typography>
                        </ListItem>
                        <ListItem>
                            <Typography level="body-sm">
                                <strong>Закрыто без покупки</strong> — незакрытый объём отменённых и частично закрытых
                                заявок
                            </Typography>
                        </ListItem>
                    </List>
                }
                watchFor={
                    <WatchSignals
                        items={[
                            {
                                signal: "Доля «Закрыто без покупки»",
                                growsIsGood: false,
                                ifGrows: "покупатели снимают заявки, не выкупив позиции; проверьте ликвидность и спред.",
                                ifFalls: "больше запрошенного объёма доходит до покупки.",
                            },
                            {
                                signal: "Доля «В резерве»",
                                growsIsGood: false,
                                ifGrows: "покупки зависают в процессе; смотрите ордера и риски.",
                                ifFalls: "резерв быстро сходит в «Куплено».",
                            },
                            {
                                signal: "Остаток за старый период",
                                growsIsGood: false,
                                ifGrows: "спрос «завис» — позиции не выкупаются; разберите в рисках, в том числе выключенные заявки.",
                                ifFalls: "остаток сходит — позиции либо купили, либо закрыли.",
                            },
                        ]}
                    />
                }
            >
                {state.loading && <BlockLoading />}
                {!state.loading && state.error && <BlockError message={state.error} onRetry={refresh} />}
                {!state.loading && !state.error && isEmpty && <EmptyState />}
                {!state.loading && !state.error && totals && !isEmpty && (
                    <>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            <MetricHint label="Создано шт." value={formatInt(totals.totalCreated)} />
                            <MetricHint label="Остаток шт." value={formatInt(totals.activeCount)} color="primary" />
                            <MetricHint label="Куплено шт." value={formatInt(totals.completedCount)} color="success" />
                            <MetricHint
                                label="В резерве"
                                value={formatInt(totals.reservedQty)}
                                color="warning"
                            />
                            <MetricHint
                                label="Закрыто без покупки"
                                value={formatInt(totals.unfilledClosedQty)}
                                color="danger"
                            />
                        </Box>

                        <Typography level="title-sm">Динамика</Typography>
                        <AnalyticsSeriesChart
                            dataset={chartData}
                            granularity={filters.granularity}
                            series={[
                                { dataKey: "totalCreated", label: "Создано", color: "#455a64" },
                                { dataKey: "completedCount", label: "Куплено", color: "#2e7d32" },
                                { dataKey: "unfilledClosedQty", label: "Закрыто без покупки", color: "#c62828" },
                                { dataKey: "activeCount", label: "Остаток", color: "#1976d2" },
                            ]}
                        />

                        {pieData.length > 0 && (
                            <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
                                <PieChart
                                    series={[
                                        {
                                            data: pieData,
                                            innerRadius: 50,
                                            outerRadius: 100,
                                            paddingAngle: 2,
                                            cornerRadius: 4,
                                            valueFormatter: (item) => formatInt(item.value),
                                        },
                                    ]}
                                    width={360}
                                    height={260}
                                    legend={{
                                        direction: "row",
                                        position: { vertical: "bottom", horizontal: "middle" },
                                    }}
                                />
                            </Box>
                        )}
                    </>
                )}
            </AnalyticsCard>
        </Box>
    );
}
